// Mock data for the qwip offsites internal concept demo.
// Nothing here is live pricing or live availability.

export type Brief = {
  teamSize: number;
  cities: { name: string; count: number }[];
  dateMode: "flexible-month" | "specific" | "not-sure";
  dateValue: string;
  format: "day" | "overnight";
  budgetTotal: number;
  budgetMode: "total" | "person";
  purpose: string;
  balance: number; // 0 = all social, 100 = all productive
  vibes: string[];
  maxTravel: string;
  requirements: string;
  wants: string;
};

export const emptyBrief: Brief = {
  teamSize: 18,
  cities: [{ name: "", count: 0 }],
  dateMode: "flexible-month",
  dateValue: "",
  format: "day",
  budgetTotal: 3000,
  budgetMode: "total",
  purpose: "",
  balance: 50,
  vibes: [],
  maxTravel: "about 2 hours",
  requirements: "",
  wants: "",
};

export const sampleBrief: Brief = {
  teamSize: 18,
  cities: [
    { name: "London", count: 8 },
    { name: "Manchester", count: 3 },
    { name: "Bristol", count: 3 },
    { name: "Birmingham", count: 2 },
    { name: "Brighton", count: 2 },
  ],
  dateMode: "flexible-month",
  dateValue: "a September weekday",
  format: "day",
  budgetTotal: 3000,
  budgetMode: "total",
  purpose: "reconnect and celebrate after a busy quarter",
  balance: 40,
  vibes: ["energetic", "low-key"],
  maxTravel: "about 2 hours for most people",
  requirements: "vegetarian and halal-friendly food, one wheelchair-accessible venue",
  wants: "something people will talk about afterwards",
};

export type TimelineItem = { time: string; title: string; detail: string };
export type BudgetLine = { label: string; amount: number };

export type Plan = {
  id: string;
  name: string;
  location: string;
  concept: string;
  total: number;
  perPerson: number;
  travelFairness: number; // 0-100
  travelLabel: string;
  balance: number;
  inclusions: string[];
  bestFor: string;
  tradeoff: string;
  memorable: number;
  productive: number;
  timeline: TimelineItem[];
  venue: { name: string; type: string; detail: string; capacity: string };
  food: string[];
  activity: { name: string; detail: string };
  evening: { name: string; detail: string };
  travel: { city: string; people: number; time: string; cost: number }[];
  budget: BudgetLine[];
  included: string[];
  notIncluded: string[];
  assumptions: string[];
  accent: "green" | "lavender" | "terracotta";
};

export const plans: Plan[] = [
  {
    id: "london-reset",
    name: "the london reset",
    location: "London, Shoreditch and Old Street",
    concept:
      "A tight, high-energy day in the city with a proper work session in the morning and a night people will bring up for months.",
    total: 2860,
    perPerson: 159,
    travelFairness: 62,
    travelLabel: "uneven",
    balance: 40,
    inclusions: [
      "Morning meeting space and breakfast",
      "Team session before lunch",
      "Collaborative afternoon activity",
      "Hijingo in the evening",
      "Optional group dinner",
    ],
    bestFor: "celebration and easy execution",
    tradeoff: "Longer travel for Manchester and Bristol attendees.",
    memorable: 92,
    productive: 70,
    accent: "green",
    timeline: [
      { time: "09:30", title: "Arrival and breakfast", detail: "Pastries, coffee and a slow start while people land from different trains." },
      { time: "10:15", title: "Quarter review session", detail: "Facilitated session in a private room with screen, whiteboards and decent chairs." },
      { time: "12:30", title: "Lunch", detail: "Long-table lunch nearby with vegetarian and halal options confirmed in advance." },
      { time: "14:00", title: "Collaborative afternoon", detail: "Mixed-team problem solving in small groups, then share-backs." },
      { time: "16:30", title: "Break and reset", detail: "Free time, walk, coffee, or drop bags before the evening." },
      { time: "18:00", title: "Hijingo", detail: "Immersive bingo with private table, food platters and a drinks tab." },
      { time: "20:30", title: "Optional dinner", detail: "Booked table for anyone staying on. Easy to skip for early trains." },
    ],
    venue: {
      name: "Old Street creative meeting space",
      type: "Private meeting room with breakout area",
      detail: "Step-free access, natural light, AV included, own kitchen for catering drop-off.",
      capacity: "Comfortable for 20 seated",
    },
    food: [
      "Breakfast: pastries, fruit, coffee cart",
      "Lunch: Middle Eastern sharing plates, halal and vegetarian by default",
      "Afternoon: snacks and drinks in the room",
      "Evening: platters at Hijingo, optional dinner after",
    ],
    activity: {
      name: "Collaborative afternoon session",
      detail: "Cross-team working session designed to mix people who rarely overlap on calls.",
    },
    evening: {
      name: "Hijingo, Shoreditch",
      detail: "Private table for 18, three games, platters and a bar tab. High energy, no planning needed on the day.",
    },
    travel: [
      { city: "London", people: 8, time: "25 mins", cost: 0 },
      { city: "Manchester", people: 3, time: "2h 10m", cost: 240 },
      { city: "Bristol", people: 3, time: "1h 40m", cost: 180 },
      { city: "Birmingham", people: 2, time: "1h 25m", cost: 100 },
      { city: "Brighton", people: 2, time: "1h 5m", cost: 60 },
    ],
    budget: [
      { label: "Venue and meeting space", amount: 620 },
      { label: "Food and drink", amount: 890 },
      { label: "Evening activity", amount: 720 },
      { label: "Travel support", amount: 430 },
      { label: "Contingency", amount: 200 },
    ],
    included: [
      "Venue hire for the working day",
      "Breakfast, lunch and afternoon snacks",
      "Hijingo private table and platters",
      "Travel contribution for out-of-London attendees",
    ],
    notIncluded: ["Accommodation", "Late-night drinks beyond the tab", "Individual expenses"],
    assumptions: [
      "Weekday in September, booked at least four weeks ahead",
      "Off-peak rail fares for out-of-London attendees",
      "18 attendees, no last-minute additions",
    ],
  },
  {
    id: "countryside-switch-off",
    name: "the countryside switch-off",
    location: "Surrey and Kent countryside, within reach of London",
    concept:
      "A calm change of scene with one shared coach out, real headspace for strategy and an afternoon outdoors together.",
    total: 3240,
    perPerson: 180,
    travelFairness: 74,
    travelLabel: "fair for most",
    balance: 55,
    inclusions: [
      "Shared coach from central London",
      "Private meeting room in a country venue",
      "Long lunch",
      "Facilitated outdoor team challenge",
      "Early evening food before returning",
    ],
    bestFor: "deeper connection and a real change of scene",
    tradeoff: "Slightly above your original budget.",
    memorable: 80,
    productive: 88,
    accent: "lavender",
    timeline: [
      { time: "08:45", title: "Coach from London", detail: "One pickup point near a main station so arriving teams roll straight on." },
      { time: "10:00", title: "Arrival and coffee", detail: "Country venue, grounds, no city noise. Slow coffee and a proper welcome." },
      { time: "10:30", title: "Strategy session", detail: "Two-part session with a break, in a private room looking onto the grounds." },
      { time: "13:00", title: "Shared lunch", detail: "Seasonal set menu at long tables, vegetarian and halal options arranged." },
      { time: "14:30", title: "Outdoor team challenge", detail: "Facilitated outdoor activity with a low-effort alternative track for anyone who prefers it." },
      { time: "17:00", title: "Early evening food", detail: "Relaxed plates and drinks before the coach back." },
      { time: "18:30", title: "Coach back to London", detail: "Home by early evening, onward trains still running." },
    ],
    venue: {
      name: "Country house meeting venue",
      type: "Private room with grounds access",
      detail: "Step-free main floor and accessible facilities, on-site catering, quiet setting.",
      capacity: "Comfortable for 24 seated",
    },
    food: [
      "Arrival: coffee, tea, fruit",
      "Lunch: seasonal set menu with vegetarian and halal mains",
      "Afternoon: drinks and snacks outdoors",
      "Early evening: sharing plates before the coach",
    ],
    activity: {
      name: "Facilitated outdoor challenge",
      detail: "Small mixed teams, light physical effort, a soft alternative for anyone who wants it.",
    },
    evening: {
      name: "Early plates and drinks",
      detail: "Relaxed sit-down before the coach so nobody is scrambling for food at a station.",
    },
    travel: [
      { city: "London", people: 8, time: "1h 15m total", cost: 0 },
      { city: "Manchester", people: 3, time: "3h 25m", cost: 240 },
      { city: "Bristol", people: 3, time: "2h 55m", cost: 180 },
      { city: "Birmingham", people: 2, time: "2h 40m", cost: 100 },
      { city: "Brighton", people: 2, time: "1h 50m", cost: 60 },
    ],
    budget: [
      { label: "Venue and meeting space", amount: 980 },
      { label: "Food and drink", amount: 1080 },
      { label: "Outdoor activity", amount: 480 },
      { label: "Coach and travel support", amount: 500 },
      { label: "Contingency", amount: 200 },
    ],
    included: [
      "Return coach from central London",
      "Private meeting room for the full day",
      "Lunch and early evening food",
      "Facilitated outdoor activity",
    ],
    notIncluded: ["Accommodation", "Rail travel into London", "Extra bar spend"],
    assumptions: [
      "Weekday booking outside school holidays",
      "One central London coach pickup point",
      "Dry-weather plan with an indoor fallback held",
    ],
  },
  {
    id: "maker-day",
    name: "the maker day",
    location: "Birmingham, a fair central meeting point",
    concept:
      "The fairest place to meet, a studio base for focused work and a hands-on afternoon that feels nothing like a corporate away day.",
    total: 2650,
    perPerson: 147,
    travelFairness: 91,
    travelLabel: "very fair",
    balance: 50,
    inclusions: [
      "Creative studio base for the day",
      "Focused team session",
      "Hands-on cooking or pottery activity",
      "Relaxed group dinner",
    ],
    bestFor: "travel fairness and a less corporate feel",
    tradeoff: "Fewer premium venue choices than central London.",
    memorable: 86,
    productive: 76,
    accent: "terracotta",
    timeline: [
      { time: "10:00", title: "Arrival and coffee", detail: "Later start so nobody needs a 6am alarm. Studio a short walk from New Street." },
      { time: "10:45", title: "Team session", detail: "Focused working session in the studio with movable furniture and wall space." },
      { time: "12:45", title: "Lunch", detail: "Local independent spot with a group table booked, plenty of vegetarian and halal choice." },
      { time: "14:15", title: "Hands-on activity", detail: "Cooking class or pottery studio, split into small mixed groups." },
      { time: "17:00", title: "Break", detail: "Free hour to check in, change or walk before dinner." },
      { time: "18:30", title: "Group dinner", detail: "Long table, set menu, no speeches unless somebody insists." },
    ],
    venue: {
      name: "Creative studio, Digbeth",
      type: "Private studio and workshop space",
      detail: "Ground floor, step-free, flexible layout, projector and plenty of wall space.",
      capacity: "Comfortable for 22",
    },
    food: [
      "Arrival: coffee and pastries",
      "Lunch: independent restaurant, group table",
      "Activity: what you cook, if you pick the cooking class",
      "Dinner: set menu with vegetarian and halal mains",
    ],
    activity: {
      name: "Cooking class or pottery studio",
      detail: "Small mixed groups, hands-on, easy conversation. People who avoid team games still enjoy this.",
    },
    evening: {
      name: "Relaxed group dinner",
      detail: "Booked long table nearby. Optional and easy to leave early for late trains.",
    },
    travel: [
      { city: "London", people: 8, time: "1h 25m", cost: 400 },
      { city: "Manchester", people: 3, time: "1h 30m", cost: 150 },
      { city: "Bristol", people: 3, time: "1h 25m", cost: 150 },
      { city: "Birmingham", people: 2, time: "15 mins", cost: 0 },
      { city: "Brighton", people: 2, time: "2h 45m", cost: 120 },
    ],
    budget: [
      { label: "Venue and studio hire", amount: 540 },
      { label: "Food and drink", amount: 820 },
      { label: "Hands-on activity", amount: 470 },
      { label: "Travel support", amount: 620 },
      { label: "Contingency", amount: 200 },
    ],
    included: [
      "Studio hire for the working day",
      "Lunch and group dinner",
      "Cooking or pottery activity",
      "Travel contribution for every city",
    ],
    notIncluded: ["Accommodation", "Late drinks", "Individual expenses"],
    assumptions: [
      "Weekday booking with advance rail fares",
      "Activity provider takes groups of 18 in two sittings",
      "18 attendees, dinner optional",
    ],
  },
];

export type Tweak = {
  id: string;
  label: string;
  reply: string;
  deltaTotal: number;
  changes: { field: string; value: string }[];
};

export const tweaks: Tweak[] = [
  {
    id: "cheaper",
    label: "make it £500 cheaper",
    reply:
      "Done. I moved the evening to a lighter format, trimmed the drinks tab and kept the working day untouched. Food quality stays where it was.",
    deltaTotal: -500,
    changes: [
      { field: "Evening", value: "Shorter evening slot with platters, no bar tab" },
      { field: "Contingency", value: "Held at 5 percent instead of 7" },
    ],
  },
  {
    id: "relaxed",
    label: "make it more relaxed",
    reply:
      "Softened it. Later start, one session instead of two and a longer lunch. The day still lands its main goal, with less on the clock.",
    deltaTotal: 60,
    changes: [
      { field: "Start", value: "10:30 arrival, no early session" },
      { field: "Balance", value: "30 productive / 70 social" },
    ],
  },
  {
    id: "outdoors",
    label: "replace Hijingo with something outdoors",
    reply:
      "Swapped. Evening is now an outdoor activity with food after, which suits your energetic and low-key mix better than a screen-led venue.",
    deltaTotal: -140,
    changes: [
      { field: "Evening", value: "Outdoor activity plus food, weather fallback held" },
      { field: "Memorable", value: "Still high, less bar-heavy" },
    ],
  },
  {
    id: "overnight",
    label: "turn this into an overnight retreat",
    reply:
      "Rebuilt as one night. Same working day, dinner together, rooms nearby and a short morning session before people travel home.",
    deltaTotal: 1980,
    changes: [
      { field: "Format", value: "One-night retreat, 18 rooms held" },
      { field: "Day two", value: "Morning session then travel home by lunch" },
    ],
  },
  {
    id: "manchester",
    label: "reduce travel for Manchester",
    reply:
      "Shifted the centre of gravity north and moved the start later. Manchester attendees now travel under 90 minutes, London absorbs a little more.",
    deltaTotal: 90,
    changes: [
      { field: "Travel fairness", value: "Improved, Manchester under 1h 30m" },
      { field: "Start", value: "10:15 arrival to fit morning trains" },
    ],
  },
];

export const pricingTiers = [
  {
    name: "explore",
    price: "free",
    sub: "",
    features: [
      "Build one AI draft",
      "Illustrative venue and activity ideas",
      "Rough travel and budget estimates",
      "Good for exploring what is possible",
    ],
    cta: "try the planner",
    popular: false,
  },
  {
    name: "plan",
    price: "£99",
    sub: "per offsite",
    features: [
      "Three complete tailored options",
      "Editable itinerary and budget",
      "Team travel comparison",
      "Shareable decision page",
      "AI plan revisions",
    ],
    cta: "build my plans",
    popular: true,
  },
  {
    name: "concierge",
    price: "from £499",
    sub: "",
    features: [
      "Real venue and supplier availability",
      "Quotes and negotiation",
      "Booking coordination",
      "Dietary and accessibility checks",
      "One point of contact through the event",
    ],
    cta: "talk to qwip",
    popular: false,
  },
];

export const faqs = [
  {
    q: "Is this only for London teams?",
    a: "No. The planner starts from where your people actually live and looks for the fairest place to meet. Plenty of plans land outside London because that is simply kinder on travel.",
  },
  {
    q: "Can it plan overnight retreats?",
    a: "Yes. One-day offsites and simple one-night retreats in the UK are the focus right now. Longer trips are on the list, not built yet.",
  },
  {
    q: "Are prices and availability live?",
    a: "Not in the planner. Costs are illustrative estimates based on typical UK pricing. Real availability and quotes come from the concierge tier.",
  },
  {
    q: "Can qwip make the bookings?",
    a: "On the concierge tier, yes. We verify availability, gather quotes and coordinate the bookings, with one point of contact through the event.",
  },
  {
    q: "What information do I need to provide?",
    a: "Team size, where people travel from, roughly when, your budget and what you want from the day. That is enough for a first set of plans. Everything else is optional.",
  },
];

export const adConcepts = [
  {
    id: "pain",
    headline: "planning the offsite shouldn’t take longer than the offsite.",
    support: "Tell qwip about your team. Get complete ideas, costs and itineraries in minutes.",
    cta: "plan an offsite",
    visual: "tabs" as const,
    note: {
      audience: "Founders, chiefs of staff and ops leads at UK remote-first companies of 10 to 60 people",
      pain: "The planning work lands on one person who already has a full job",
      channel: "LinkedIn single image, targeted by job title and company size",
      cta: "plan an offsite",
    },
  },
  {
    id: "cities",
    headline: "your team lives in eight cities. where should you meet?",
    support: "qwip balances travel, budget and the kind of day you actually want.",
    cta: "find the fairest place",
    visual: "map" as const,
    note: {
      audience: "Distributed UK teams with no clear home office",
      pain: "Choosing a location feels political and travel fairness is hard to calculate",
      channel: "LinkedIn and Instagram, retargeting site visitors",
      cta: "find the fairest place",
    },
  },
  {
    id: "outcome",
    headline: "18 teammates. one budget. three complete plans.",
    support: "Not a list of venues. Complete days with costs, travel and an agenda.",
    cta: "see an example plan",
    visual: "cards" as const,
    note: {
      audience: "People who have already tried planning an offsite themselves",
      pain: "Venue lists and directories do not solve the actual problem",
      channel: "LinkedIn carousel or single image, cold traffic",
      cta: "see an example plan",
    },
  },
  {
    id: "memorable",
    headline: "strategy session. good lunch. Hijingo.",
    support: "Plan the productive part and the part people will talk about afterwards.",
    cta: "plan an offsite",
    visual: "timeline" as const,
    note: {
      audience: "Culture and people leads who care how the day feels",
      pain: "Offsites tend to be all workshop and no joy, or all party and no point",
      channel: "Instagram 4:5 and Stories, warm audiences",
      cta: "plan an offsite",
    },
  },
];
